import type {
  CallOutcome,
  MobileCall,
  SubmitCallResultInput,
  SubmitCallResultResponse,
} from './api.js';
import {
  GUAYAQUIL_TIME_ZONE,
} from './calendar.js';
import { whatsappChatUrl } from './call-screen.js';
import { contactActionLabel, openVCardContact } from './vcard.js';
import { configHilo, subirGrabacion } from './hilo.js';

export interface PostCallScreenActions {
  submit: (input: SubmitCallResultInput) => Promise<SubmitCallResultResponse>;
  recordContactOpened: () => Promise<void>;
  downloadContact: (contents: string, filename: string) => void;
  openDialer?: (url: string) => void;
  now?: () => Date;
}

export interface PostCallScreenController {
  showResult(response: SubmitCallResultResponse): void;
  showMessage(
    message: string,
    tone?: 'pending' | 'error',
    locked?: boolean,
  ): void;
}

export class PostCallSubmissionError extends Error {
  constructor(
    message: string,
    readonly tone: 'pending' | 'error',
  ) {
    super(message);
    this.name = 'PostCallSubmissionError';
  }
}

const outcomeLabels: Record<CallOutcome, string> = {
  no_answer: 'No contestó',
  answered: 'Sí contestó',
  not_interested: 'No le interesa',
};

function actionButton(
  label: string,
  outcome: CallOutcome,
  className: string,
): HTMLButtonElement {
  const node = document.createElement('button');
  node.type = 'button';
  node.className = `outcome-button ${className}`;
  node.dataset.outcome = outcome;
  node.textContent = label;
  node.setAttribute('aria-label', `Registrar resultado: ${label}`);
  return node;
}

function formatNextActivity(value: string): string {
  return new Intl.DateTimeFormat('es-EC', {
    timeZone: GUAYAQUIL_TIME_ZONE,
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function successfulMessage(response: SubmitCallResultResponse): string {
  const outcome = outcomeLabels[response.outcome];
  const next = response.nextActivityAt
    ? ` Próxima llamada: ${formatNextActivity(response.nextActivityAt)}.`
    : '';
  return `Resultado registrado: ${outcome}.${next}`;
}

export function renderPostCallScreen(
  root: HTMLElement,
  call: MobileCall,
  actions: PostCallScreenActions,
): PostCallScreenController {
  let submitting = false;
  let confirmation: HTMLElement | null = null;

  root.replaceChildren();
  const card = document.createElement('section');
  card.className = 'card post-call-card';
  card.setAttribute('aria-labelledby', 'post-call-title');

  const eyebrow = document.createElement('p');
  eyebrow.className = 'eyebrow';
  eyebrow.textContent = 'Después de llamar';
  const title = document.createElement('h1');
  title.id = 'post-call-title';
  title.textContent = '¿Qué ocurrió con la llamada?';
  const customer = document.createElement('p');
  customer.className = 'post-call-customer';
  customer.textContent = call.projectName.trim()
    ? `${call.customerName} — ${call.projectName.trim()}`
    : call.customerName;
  const phone = document.createElement('p');
  phone.className = 'phone-number';
  phone.textContent = call.phoneNumber;
  const hint = document.createElement('p');
  hint.className = 'description';
  hint.textContent = 'Elige lo que ocurrió. La app no supone que la llamada terminó ni cuál fue el resultado.';

  const retryPanel = document.createElement('section');
  retryPanel.className = 'second-attempt-panel';
  const retryTitle = document.createElement('h2');
  retryTitle.textContent = 'Segundo intento opcional';
  const retryHint = document.createElement('p');
  retryHint.className = 'field-hint';
  retryHint.textContent = 'Si no contestó, puedes llamarlo una vez más antes de registrar el resultado.';
  const retryActions = document.createElement('div');
  retryActions.className = 'call-actions';
  const retrySim = document.createElement('button');
  retrySim.type = 'button';
  retrySim.className = 'secondary-button';
  retrySim.dataset.action = 'retry-sim';
  retrySim.dataset.retryCall = '';
  retrySim.textContent = 'Segundo intento con SIM';
  const retryWhatsapp = document.createElement('button');
  retryWhatsapp.type = 'button';
  retryWhatsapp.className = 'secondary-button whatsapp-outline-button';
  retryWhatsapp.dataset.action = 'retry-whatsapp';
  retryWhatsapp.dataset.retryCall = '';
  retryWhatsapp.textContent = 'Segundo intento por WhatsApp';
  retrySim.disabled = actions.openDialer === undefined;
  retryWhatsapp.disabled = actions.openDialer === undefined;
  retrySim.addEventListener('click', () => actions.openDialer?.(`tel:${call.phoneNumber}`));
  retryWhatsapp.addEventListener('click', () => actions.openDialer?.(whatsappChatUrl(call.phoneNumber)));
  retryActions.append(retrySim, retryWhatsapp);
  retryPanel.append(retryTitle, retryHint, retryActions);

  const outcomes = document.createElement('div');
  outcomes.className = 'outcome-grid';
  outcomes.setAttribute('aria-label', 'Resultado de la llamada');
  const noAnswer = actionButton('No contestó', 'no_answer', 'outcome-neutral');
  const answered = actionButton('Sí contestó', 'answered', 'outcome-positive');
  const notInterested = actionButton(
    'No le interesa',
    'not_interested',
    'outcome-negative',
  );
  outcomes.append(noAnswer, answered, notInterested);

  const details = document.createElement('details');
  details.className = 'comment-details';
  const summary = document.createElement('summary');
  summary.textContent = 'Agregar comentario (opcional)';
  const commentLabel = document.createElement('label');
  commentLabel.htmlFor = 'call-result-comment';
  commentLabel.textContent = 'Comentario para la línea de tiempo';
  const comment = document.createElement('textarea');
  comment.id = 'call-result-comment';
  comment.rows = 4;
  comment.placeholder = 'Escribe solo si hace falta agregar contexto';
  comment.setAttribute('aria-describedby', 'comment-count');
  const count = document.createElement('output');
  count.id = 'comment-count';
  count.dataset.commentCount = '';
  count.className = 'character-count';
  count.setAttribute('aria-live', 'polite');
  count.textContent = '0 de 2000';
  comment.addEventListener('input', () => {
    const characters = Array.from(comment.value);
    if (characters.length > 2_000) comment.value = characters.slice(0, 2_000).join('');
    count.textContent = `${Array.from(comment.value).length} de 2000`;
  });
  details.append(summary, commentLabel, comment, count);

  const contactButton = document.createElement('button');
  contactButton.type = 'button';
  contactButton.className = 'secondary-button';
  contactButton.dataset.action = 'save-contact';
  contactButton.textContent = contactActionLabel(call.contactPreviouslyOpened);
  const contactStatus = document.createElement('p');
  contactStatus.className = 'status contact-status';
  contactStatus.setAttribute('role', 'status');
  contactStatus.setAttribute('aria-live', 'polite');

  const status = document.createElement('p');
  status.className = 'status post-call-status';
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');
  status.tabIndex = -1;

  const allButtons = () =>
    Array.from(card.querySelectorAll<HTMLButtonElement>(
      '[data-outcome], [data-retry-call], .confirmation-panel button',
    ));
  const setControlsDisabled = (value: boolean) => {
    submitting = value;
    for (const button of allButtons()) button.disabled = value;
    comment.disabled = value;
  };
  const setSubmitting = (value: boolean) => {
    card.setAttribute('aria-busy', String(value));
    setControlsDisabled(value);
  };

  const controller: PostCallScreenController = {
    showResult(response) {
      setSubmitting(false);
      outcomes.hidden = true;
      details.hidden = true;
      retryPanel.hidden = true;
      confirmation?.remove();
      confirmation = null;
      status.className = 'status success post-call-status';
      status.setAttribute('role', 'status');
      status.textContent = successfulMessage(response);
      status.focus();
    },
    showMessage(message, tone = 'error', locked = false) {
      if (locked) {
        card.setAttribute('aria-busy', 'false');
        setControlsDisabled(true);
      } else {
        setSubmitting(false);
      }
      status.className = `status ${tone === 'error' ? 'error' : 'pending'} post-call-status`;
      status.setAttribute('role', tone === 'error' ? 'alert' : 'status');
      status.textContent = message;
    },
  };

  const submit = async (input: SubmitCallResultInput) => {
    if (submitting) return;
    confirmation?.remove();
    confirmation = null;
    setSubmitting(true);
    status.className = 'status post-call-status';
    status.setAttribute('role', 'status');
    status.textContent = 'Guardando el resultado en Bitrix…';
    try {
      controller.showResult(await actions.submit(input));
    } catch (error) {
      controller.showMessage(
        error instanceof Error
          ? error.message
          : 'No se pudo guardar el resultado. Intenta nuevamente.',
        error instanceof PostCallSubmissionError ? error.tone : 'error',
        error instanceof PostCallSubmissionError,
      );
    }
  };

  let openingContact = false;
  contactButton.addEventListener('click', async () => {
    if (openingContact) return;
    openingContact = true;
    contactButton.disabled = true;
    contactStatus.textContent = 'Preparando la tarjeta de contacto…';
    try {
      const { trackingFailed } = await openVCardContact(call, actions);
      contactStatus.textContent = trackingFailed
        ? 'Tarjeta lista. No se pudo guardar el recordatorio en la app.'
        : 'Tarjeta de contacto lista para guardar.';
    } catch {
      contactStatus.textContent = 'No se pudo preparar la tarjeta de contacto.';
    } finally {
      openingContact = false;
      contactButton.disabled = false;
    }
  });

  noAnswer.addEventListener('click', () => {
    void submit({
      outcome: 'no_answer',
      nextActivityAt: null,
      comment: comment.value,
    });
  });
  answered.addEventListener('click', () => {
    void submit({
      outcome: 'answered',
      nextActivityAt: null,
      comment: comment.value,
    });
  });
  notInterested.addEventListener('click', () => {
    if (confirmation) return;
    confirmation = document.createElement('div');
    confirmation.className = 'confirmation-panel';
    confirmation.setAttribute('role', 'alertdialog');
    confirmation.setAttribute('aria-labelledby', 'confirm-not-interested-title');
    confirmation.setAttribute('aria-describedby', 'confirm-not-interested-description');
    const confirmationTitle = document.createElement('h2');
    confirmationTitle.id = 'confirm-not-interested-title';
    confirmationTitle.textContent = '¿Confirmas que no le interesa?';
    const confirmationDescription = document.createElement('p');
    confirmationDescription.id = 'confirm-not-interested-description';
    confirmationDescription.textContent = 'Esto registrará la llamada y moverá el negocio a “No le interesa”.';
    const confirm = document.createElement('button');
    confirm.type = 'button';
    confirm.className = 'danger-button';
    confirm.dataset.action = 'confirm-not-interested';
    confirm.textContent = 'Sí, registrar No le interesa';
    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.className = 'secondary-button';
    cancel.textContent = 'Cancelar';
    confirm.addEventListener('click', () => {
      void submit({
        outcome: 'not_interested',
        nextActivityAt: null,
        comment: comment.value,
      });
    });
    cancel.addEventListener('click', () => {
      confirmation?.remove();
      confirmation = null;
      notInterested.focus();
    });
    confirmation.append(
      confirmationTitle,
      confirmationDescription,
      confirm,
      cancel,
    );
    outcomes.after(confirmation);
    confirm.focus();
  });

  card.append(
    eyebrow,
    title,
    customer,
    phone,
    hint,
    retryPanel,
    outcomes,
    details,
    contactButton,
    contactStatus,
    status,
  );

  // --- Grabación de la llamada (HILO) --------------------------------------
  // La llamada la graba iOS con su botón nativo; aquí solo se recibe el
  // archivo que el vendedor comparte y se sube ENLAZADO a esta llamada
  // (externo_id = call.id). Reintentar no duplica: HILO es idempotente por
  // esa referencia. Sin configuración de HILO, este panel no existe.
  const hilo = configHilo();
  if (hilo) {
    const recPanel = document.createElement('section');
    recPanel.className = 'second-attempt-panel recording-panel';
    const recTitle = document.createElement('h2');
    recTitle.textContent = 'Grabación de la llamada';
    // LO PUNTUAL E IMPORTANTE: a quién llamó, con nombre, sin adivinar.
    const recQuien = document.createElement('p');
    recQuien.className = 'post-call-customer recording-quien';
    recQuien.textContent = `Llamaste a ${call.customerName}`;
    const recHint = document.createElement('p');
    recHint.className = 'field-hint';
    recHint.textContent = 'Grabaste con el botón de iOS: el archivo está en Notas → Grabaciones de llamadas. Súbelo aquí y HILO lo transcribe y lo resume.';
    const recInput = document.createElement('input');
    recInput.type = 'file';
    recInput.accept = 'audio/*,.m4a';
    recInput.id = 'grabacion-archivo';
    recInput.hidden = true;
    const recButton = document.createElement('button');
    recButton.type = 'button';
    recButton.className = 'primary-button';
    recButton.dataset.action = 'upload-recording';
    recButton.textContent = 'Subir grabación';
    const recStatus = document.createElement('p');
    recStatus.className = 'status';
    recStatus.setAttribute('role', 'status');
    recStatus.setAttribute('aria-live', 'polite');
    let subiendo = false;
    recButton.addEventListener('click', () => { if (!subiendo) recInput.click(); });
    recInput.addEventListener('change', async () => {
      const archivo = recInput.files?.[0];
      if (!archivo || subiendo) return;
      subiendo = true;
      recButton.disabled = true;
      recStatus.className = 'status';
      recStatus.textContent = 'Subiendo la grabación…';
      try {
        const r = await subirGrabacion(hilo, archivo, {
          externoId: call.id,
          // El nombre viaja DENTRO de HILO con la llamada importada.
          nota: `Llamada con ${call.customerName} (${call.phoneNumber})`,
        });
        recStatus.className = 'status success';
        recStatus.textContent = r.duplicada
          ? `Esa grabación ya estaba subida con ${call.customerName}. Todo en orden.`
          : `Grabación de ${call.customerName} subida. HILO la transcribe en unos minutos.`;
      } catch (error) {
        recStatus.className = 'status error';
        recStatus.textContent = error instanceof Error
          ? error.message
          : 'No se pudo subir. Vuelve a intentarlo.';
      } finally {
        subiendo = false;
        recButton.disabled = false;
        recInput.value = '';
      }
    });
    recPanel.append(recTitle, recQuien, recHint, recButton, recInput, recStatus);
    card.append(recPanel);
  }

  root.append(card);
  return controller;
}
